import React, { useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import PrintIcon from "@mui/icons-material/Print";

const STORE = {
  name:    "KR Carimbos e Gráfica",
  address: "R. Álvares Maciel, 343 — Santa Efigênia — Belo Horizonte/MG",
  phone:   "(31) 3224-3820",
  email:   "carimbos@krcarimbos.com.br",
};

const fmtDate  = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
const fmtMoney = (v) => v != null ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—";
const fmtPM    = (pm) => { if (!pm) return null; try { const p = JSON.parse(pm); return p.nome || p.descricao || pm; } catch { return pm; } };
const fmtDT    = (d)  => d.toLocaleDateString("pt-BR") + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

const printStyles = `
  @page { size: A4 landscape; margin: 8mm 10mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #000; background: #fff; }

  .hdr { display:flex; justify-content:space-between; align-items:flex-start;
         border-bottom:2.5px solid #000; padding-bottom:8px; margin-bottom:11px; }
  .hdr-left { display:flex; align-items:flex-start; gap:14px; }
  .hdr-logo-img { height:52px; width:auto; object-fit:contain; }
  .hdr-logo  { font-size:26px; font-weight:900; letter-spacing:-1px; }
  .hdr-store { font-size:11.5px; color:#333; margin-top:4px; line-height:1.65; }
  .hdr-right { text-align:right; }
  .hdr-num   { font-size:28px; font-weight:900; font-family:monospace; }
  .hdr-emit  { font-size:11.5px; color:#333; margin-top:3px; }
  .badge-valid { display:inline-block; padding:2px 9px; border:1.5px solid #000;
                 border-radius:10px; font-size:10px; font-weight:700; margin-top:4px; }

  .cols2 { display:grid; grid-template-columns:1fr 1fr; gap:0 28px; margin-bottom:11px; }
  .cols3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:4px 16px; }

  .sec-title { font-size:10px; font-weight:700; text-transform:uppercase;
               letter-spacing:.7px; color:#444; border-bottom:1px solid #aaa;
               padding-bottom:3px; margin-bottom:6px; }

  .lbl { font-size:10px; color:#555; margin-bottom:1px; }
  .val { font-size:14px; font-weight:600; }
  .val-lg { font-size:17px; font-weight:700; }

  table { width:100%; border-collapse:collapse; margin-bottom:11px; }
  th { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px;
       color:#444; border-top:1.5px solid #aaa; border-bottom:1.5px solid #aaa;
       padding:4px 7px; text-align:left; }
  td { font-size:13px; padding:4px 7px; border-bottom:1px solid #e5e5e5; }
  .tr { text-align:right; }
  .total td { font-weight:800; font-size:15px; border-top:2px solid #000;
              border-bottom:none; padding:5px 7px; }

  .obs { border:1px solid #bbb; border-radius:3px; padding:6px 9px;
         font-size:12px; white-space:pre-wrap; min-height:32px; margin-bottom:11px; }

  .sigs { display:flex; gap:28px; margin-top:20px; }
  .sig  { flex:1; border-top:1px solid #000; padding-top:5px;
          font-size:10px; color:#555; text-align:center; }

  .footer { border-top:1px solid #bbb; margin-top:10px; padding-top:5px;
            font-size:10px; color:#555; display:flex; justify-content:space-between; }

  .notice { border:1.5px dashed #aaa; border-radius:4px; padding:6px 10px;
            font-size:10px; color:#555; margin-bottom:11px; }

  /* Marca d'água */
  .watermark {
    position: fixed; top: 50%; left: 50%;
    transform: translate(-50%,-50%) rotate(-45deg);
    font-size: 96px; font-weight: 900; letter-spacing: 4px;
    opacity: 0.09; pointer-events: none; white-space: nowrap;
    z-index: 9999; text-transform: uppercase; font-family: Arial, sans-serif;
  }
`;

const QuotePrint = ({ quote, onClose }) => {
  const printRef = useRef();
  const [emissao] = useState(() => new Date());

  const handlePrint = () => {
    const html = [
      "<!DOCTYPE html><html><head>",
      '<meta charset="utf-8"/>',
      `<title>Orçamento ${quote.numero}</title>`,
      `<style>${printStyles}</style>`,
      "</head><body>",
      printRef.current.innerHTML,
      "</body></html>",
    ].join("");
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, "_blank");
    if (win) win.addEventListener("load", () => setTimeout(() => { win.print(); URL.revokeObjectURL(url); }, 300));
    else URL.revokeObjectURL(url);
  };

  const clientName  = quote.clientName  || quote.contact?.name  || "—";
  const clientPhone = quote.clientPhone || quote.contact?.number || "—";
  const items = Array.isArray(quote.items) ? quote.items.filter(it => it.descricao) : [];
  const itemsSubtotal = items.reduce((s, it) => s + (parseFloat(it.valorTotal) || 0), 0);
  const freteAtivo = Boolean(quote.freteAtivo);
  const freteValor = freteAtivo ? (parseFloat(quote.freteValor) || 0) : 0;
  const grossTotal = quote.total != null
    ? Number(quote.total)
    : itemsSubtotal + freteValor;
  // Desconto (R$ fixo) embutido no paymentMethod JSON — abater do total bruto.
  let descontoVal = 0;
  try { const p = JSON.parse(quote.paymentMethod || "{}"); descontoVal = Number(p?.desconto) || 0; } catch { /* free-text */ }
  const total = Math.max(0, grossTotal - descontoVal);

  const QUOTE_WM = {
    aceito:   { text: "ACEITO",    color: "#1b5e20" },
    recusado: { text: "RECUSADO",  color: "#b71c1c" },
    pendente: { text: "A RECEBER", color: "#e65100" },
  };
  const wm = QUOTE_WM[quote.status];

  const S = {
    hdr:       { display:"flex", justifyContent:"space-between", alignItems:"flex-start", borderBottom:"2.5px solid #000", paddingBottom:8, marginBottom:11 },
    hdrLogo:   { fontSize:28, fontWeight:900, letterSpacing:-1 },
    hdrStore:  { fontSize:13, color:"#444", marginTop:4, lineHeight:1.65 },
    hdrRight:  { textAlign:"right" },
    hdrNum:    { fontSize:30, fontWeight:900, fontFamily:"monospace" },
    hdrLabel:  { fontSize:12, color:"#555", marginBottom:2, textTransform:"uppercase", letterSpacing:1 },
    hdrEmit:   { fontSize:13, color:"#333", marginTop:3 },
    badgeValid:{ display:"inline-block", padding:"2px 9px", border:"1.5px solid #000", borderRadius:10, fontSize:11, fontWeight:700, marginTop:4 },
    cols2:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 28px", marginBottom:11 },
    cols3:     { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"4px 16px" },
    secTitle:  { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:.7, color:"#444", borderBottom:"1px solid #aaa", paddingBottom:3, marginBottom:6 },
    lbl:       { fontSize:11, color:"#555", marginBottom:1 },
    val:       { fontSize:15, fontWeight:600 },
    valLg:     { fontSize:18, fontWeight:700 },
    total:     { fontWeight:800, fontSize:16, borderTop:"2px solid #000", borderBottom:"none", padding:"5px 7px" },
    obs:       { border:"1px solid #bbb", borderRadius:3, padding:"6px 9px", fontSize:13, whiteSpace:"pre-wrap", minHeight:32, marginBottom:11 },
    notice:    { border:"1.5px dashed #aaa", borderRadius:4, padding:"6px 10px", fontSize:11, color:"#555", marginBottom:11 },
    sigs:      { display:"flex", gap:28, marginTop:20 },
    sig:       { flex:1, borderTop:"1px solid #000", paddingTop:5, fontSize:11, color:"#555", textAlign:"center" },
    footer:    { borderTop:"1px solid #bbb", marginTop:10, paddingTop:5, fontSize:11, color:"#555", display:"flex", justifyContent:"space-between" },
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <div style={{ padding:"20px 24px 0", background:"#fff", overflowX:"auto" }}>
        <div ref={printRef} style={{ minWidth:700 }}>

          {/* ── MARCA D'ÁGUA ── */}
          {wm && (
            <div style={{
              position: "fixed", top: "50%", left: "50%",
              transform: "translate(-50%,-50%) rotate(-45deg)",
              fontSize: 100, fontWeight: 900, letterSpacing: 4,
              opacity: 0.09, pointerEvents: "none", whiteSpace: "nowrap",
              zIndex: 9999, textTransform: "uppercase",
              color: wm.color, userSelect: "none",
            }}>
              {wm.text}
            </div>
          )}

          {/* ── CABEÇALHO ── */}
          <div style={S.hdr}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
              <img
                src="https://adm.graficakr.com.br/logo.png"
                alt={STORE.name}
                style={{ height:52, width:"auto", objectFit:"contain", flexShrink:0 }}
              />
              <div>
                <div style={S.hdrLogo}>{STORE.name}</div>
                <div style={S.hdrStore}>
                  {STORE.address}<br/>
                  {STORE.phone} · {STORE.email}
                </div>
              </div>
            </div>
            <div style={S.hdrRight}>
              <div style={S.hdrLabel}>Orçamento</div>
              <div style={S.hdrNum}>{quote.numero}</div>
              <div style={S.hdrEmit}>Emitido em: {fmtDT(emissao)}</div>
              {quote.validUntil && (
                <div style={S.badgeValid}>Válido até {fmtDate(quote.validUntil)}</div>
              )}
            </div>
          </div>

          {/* ── AVISO DE VALIDADE ── */}
          <div style={S.notice}>
            Este documento é um orçamento e não constitui nota fiscal. Os valores são válidos
            {quote.validUntil ? ` até ${fmtDate(quote.validUntil)}` : " por prazo determinado"}.
            Sujeito a alteração sem aviso prévio.
          </div>

          {/* ── CLIENTE + DADOS (2 colunas) ── */}
          <div style={S.cols2}>
            <div>
              <div style={S.secTitle}>Cliente</div>
              <div style={{ marginBottom:6 }}>
                <div style={S.lbl}>Nome</div>
                <div style={S.valLg}>{clientName}</div>
              </div>
              <div>
                <div style={S.lbl}>Telefone</div>
                <div style={S.val}>{clientPhone}</div>
              </div>
            </div>

            <div>
              <div style={S.secTitle}>Detalhes do orçamento</div>
              <div style={S.cols3}>
                {fmtPM(quote.paymentMethod) && (
                  <div>
                    <div style={S.lbl}>Forma de pagamento</div>
                    <div style={S.val}>{fmtPM(quote.paymentMethod)}</div>
                  </div>
                )}
                {quote.responsible?.name && (
                  <div>
                    <div style={S.lbl}>Atendente</div>
                    <div style={S.val}>{quote.responsible.name}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── ITENS ── */}
          {items.length > 0 && (
            <>
              <div style={S.secTitle}>Itens do orçamento</div>
              <table style={{ width:"100%", borderCollapse:"collapse", marginBottom:11 }}>
                <thead>
                  <tr>
                    {[["Descrição","left",""],["Qtd","right","52px"],["V. Unit.","right","95px"],["Total","right","105px"]].map(([h,a,w]) => (
                      <th key={h} style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.5, color:"#444", borderTop:"1.5px solid #aaa", borderBottom:"1.5px solid #aaa", padding:"4px 7px", textAlign:a, width:w||undefined }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5" }}>{it.descricao}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{it.qtd}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{fmtMoney(it.valorUnit)}</td>
                      <td style={{ fontSize:14, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>{fmtMoney(it.valorTotal)}</td>
                    </tr>
                  ))}
                  {freteAtivo && freteValor > 0 && (
                    <tr>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", color:"#555", fontStyle:"italic" }}>
                        Frete{quote.freteEndereco ? ` — ${quote.freteEndereco}` : ""}{quote.freteCep ? ` (CEP ${quote.freteCep})` : ""}
                      </td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>—</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right" }}>—</td>
                      <td style={{ fontSize:13, padding:"4px 7px", borderBottom:"1px solid #e5e5e5", textAlign:"right", fontWeight:600 }}>{fmtMoney(freteValor)}</td>
                    </tr>
                  )}
                  {descontoVal > 0 && (
                    <>
                      <tr>
                        <td colSpan={3} style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#555" }}>Subtotal</td>
                        <td style={{ fontSize:13, padding:"3px 7px", textAlign:"right" }}>{fmtMoney(grossTotal)}</td>
                      </tr>
                      <tr>
                        <td colSpan={3} style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#0a7d3e", fontWeight:600 }}>Desconto</td>
                        <td style={{ fontSize:13, padding:"3px 7px", textAlign:"right", color:"#0a7d3e", fontWeight:600 }}>-{fmtMoney(descontoVal)}</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan={3} style={{ ...S.total, textAlign:"right" }}>TOTAL</td>
                    <td style={{ ...S.total, textAlign:"right" }}>{fmtMoney(total)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* ── OBSERVAÇÕES ── */}
          {quote.observations && (
            <>
              <div style={S.secTitle}>Observações</div>
              <div style={S.obs}>{quote.observations}</div>
            </>
          )}

          {/* ── ASSINATURA ── */}
          <div style={S.sigs}>
            {["Aprovação do cliente / Data", "Responsável pela proposta"].map(label => (
              <div key={label} style={S.sig}>{label}</div>
            ))}
          </div>

          {/* ── RODAPÉ ── */}
          <div style={S.footer}>
            <span>{STORE.name} — {STORE.address}</span>
            <span>{STORE.phone} · {STORE.email}</span>
          </div>

        </div>
      </div>

      <DialogActions style={{ padding:"10px 24px" }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
          style={{ backgroundColor:"#333", color:"#fff" }}>
          Imprimir / PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotePrint;
